import { useTranslation } from 'react-i18next';
import './login-pa.css';

export type IdpHint = 'spid' | 'cie' | 'cns';

export interface LoginPaProps {
  /** Avvia il redirect OIDC verso Keycloak per l'IdP scelto. */
  onLogin: (idp: IdpHint) => void;
  /** Disabilita i bottoni durante il redirect. */
  pending?: IdpHint | null;
  /** Nasconde la fascia del ciclo a 4 fasi (hero più corto). */
  showLifecycle?: boolean;
  /** Nasconde la fascia dei fondi. */
  showFunds?: boolean;
}

const CYCLE = ['design', 'implementation', 'control', 'reporting'] as const;
const FUNDS = ['erdf', 'esf', 'eafrd', 'other'] as const;

export function LoginPa({
  onLogin,
  pending = null,
  showLifecycle = true,
  showFunds = true,
}: LoginPaProps) {
  const { t, i18n } = useTranslation();

  return (
    <div className="urania-login">
      <header className="urania-login__hdr">
        <div className="urania-login__lockup">
          <span className="urania-login__mark" aria-hidden="true" />
          <span className="urania-login__wordmark">
            URAN<span>IA</span> PA
          </span>
        </div>

        <nav aria-label={t('login.nav.label')}>
          <a href="/about">{t('login.nav.about')}</a>
          <a href="/assistenza">{t('login.nav.support')}</a>
          <span className="urania-login__vr" aria-hidden="true" />
          {(['it', 'en'] as const).map((lng) => (
            <button
              key={lng}
              type="button"
              className={`urania-login__lang${i18n.resolvedLanguage === lng ? ' is-active' : ''}`}
              aria-current={i18n.resolvedLanguage === lng ? 'true' : undefined}
              onClick={() => void i18n.changeLanguage(lng)}
            >
              {lng.toUpperCase()}
            </button>
          ))}
        </nav>
      </header>

      <main className="urania-login__main">
        <div className="container-fluid">
          <div className="row justify-content-between g-4 g-xl-5">
            <div className="col-12 col-lg-7 col-xl-7">
              <p className="urania-login__eyebrow">{t('login.eyebrow')}</p>
              <h1 className="urania-login__title">
                {t('login.title.line1')} <em>{t('login.title.emphasis')}</em>.
                <br />
                {t('login.title.line2')}
              </h1>
              <p className="urania-login__lede">{t('login.lede')}</p>

              {showLifecycle && (
                <ol className="urania-login__cycle">
                  {CYCLE.map((key, i) => (
                    <li key={key}>
                      <span className="urania-login__cycle-num">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="urania-login__cycle-lbl">{t(`login.cycle.${key}`)}</span>
                    </li>
                  ))}
                </ol>
              )}

              {showFunds && (
                <ul className="urania-login__funds">
                  {FUNDS.map((key) => (
                    <li key={key}>
                      <span className="urania-login__funds-amt">{t(`login.funds.${key}.abbr`)}</span>
                      <span className="urania-login__funds-cap">{t(`login.funds.${key}.name`)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="col-12 col-lg-5 col-xl-4">
              <section className="urania-login__auth" aria-labelledby="urania-auth-title">
                <h2 id="urania-auth-title" className="urania-login__auth-title">
                  {t('login.auth.title')}
                </h2>
                <p className="urania-login__auth-sub">{t('login.auth.subtitle')}</p>

                <button
                  type="button"
                  className="urania-login__btn-spid"
                  disabled={pending !== null}
                  aria-busy={pending === 'spid'}
                  onClick={() => onLogin('spid')}
                >
                  <span className="urania-login__tag">SPID</span>
                  {pending === 'spid' ? t('login.auth.redirecting') : t('login.auth.spid')}
                </button>

                <p className="urania-login__assurance">
                  <span className="urania-login__dot" aria-hidden="true" />
                  {t('login.auth.spidHint')}
                </p>

                <p className="urania-login__or">
                  <span>{t('login.auth.or')}</span>
                </p>

                <div className="urania-login__methods">
                  {(['cie', 'cns'] as const).map((idp) => (
                    <button
                      key={idp}
                      type="button"
                      className="urania-login__btn-method"
                      disabled={pending !== null}
                      aria-busy={pending === idp}
                      onClick={() => onLogin(idp)}
                    >
                      <span className="urania-login__tag">{idp.toUpperCase()}</span>
                      <span className="urania-login__method-txt">
                        <span className="urania-login__method-name">{t(`login.auth.${idp}.name`)}</span>
                        <span className="urania-login__method-hint">{t(`login.auth.${idp}.hint`)}</span>
                      </span>
                    </button>
                  ))}
                </div>

                <hr className="urania-login__rule" />

                <p className="urania-login__auth-links">
                  <a href="/assistenza/accesso">{t('login.auth.trouble')}</a>
                  <a href="/assurance">{t('login.auth.assurance')}</a>
                  <a href="/login/staging">Accesso tecnico di collaudo</a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <footer className="urania-login__ftr">
        <span className="urania-login__aitag">AI ACT</span>
        <p>
          {t('login.aiAct.body')}{' '}
          <a href="/normativa">{t('login.aiAct.link')}</a>
        </p>
        <p className="urania-login__ftr-links">
          <a href="/privacy">{t('login.footer.privacy')}</a>
          <a href="/accessibilita">{t('login.footer.accessibility')}</a>
          <a href="/note-legali">{t('login.footer.legal')}</a>
        </p>
      </footer>
    </div>
  );
}

export default LoginPa;
